import { useLoginStore } from '../../../store/useLogin';
import { useIframeStore } from '../../../store/useIframe';
import { usePageAlertStore } from '../../../store/usePageAlert';
import { useLocation } from 'react-router-dom';
import { getIframeConfig } from '../../../data/iframeUrls';
import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';

/**
 * @author taulmax
 * @date 2024.10.07
 * @description 레이아웃 제목 컴포넌트
 */
const Title = ({ menu }: { menu?: string }) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  const isDev =
    baseURL === 'https://server-dev.sajutalktalk.com' ? ' (개발서버)' : '';

  const { toggleIframe, isOpen, setIframeUrl, openIframe, openDefaultIframe, closeIframe, isAutoOpened } = useIframeStore();
  const {
    alertType, alertMessage, alertSubMessage, onAlertClick,
    showButton, buttonLabel, buttonLoading, onButtonClick,
    showSecondaryButton, secondaryButtonLabel, secondaryButtonLoading, secondaryButtonDisabled, onSecondaryButtonClick
  } = usePageAlertStore();
  const location = useLocation();
  
  // 현재 페이지의 iframe 설정 가져오기
  const iframeConfig = getIframeConfig(location.pathname);
  
  // 페이지 변경 시 iframe URL 업데이트 및 자동 열기 처리
  useEffect(() => {
    if (!iframeConfig) {
      // iframe 설정이 없는 페이지는 iframe 닫기
      closeIframe();
      return;
    }
    
    // 기본 URL이 있으면 기본 URL 사용, 없으면 일반 URL 사용
    const urlToSet = iframeConfig.defaultUrl || iframeConfig.url;
    setIframeUrl(urlToSet);
    
    // 기본 URL이 있는 페이지는 자동으로 iframe 열기
    if (iframeConfig.defaultUrl) {
      openDefaultIframe(urlToSet);
    } else {
      // 기본 URL이 없는 페이지로 이동하면 iframe 닫기
      closeIframe();
    }
  }, [location.pathname, setIframeUrl, iframeConfig, openDefaultIframe, closeIframe]);
  
  const handleToggleIframe = () => {
    if (!iframeConfig) return;
    
    // 새 탭으로 열어야 하는 페이지인 경우 (/bizmsg/sender)
    if (iframeConfig.openInNewTab) {
      window.open(iframeConfig.url, '_blank');
      return;
    }

    // 기존 iframe 방식
    if (!isOpen) {
      openIframe(iframeConfig.url);
    } else {
      toggleIframe();
    }
  };

  return (
    <div className="!flex !items-center !pl-14 md:!pl-6 !pr-6 !h-[70px] !gap-4">
      {/* 페이지 제목 */}
      <h2 className="!text-[30px] !font-bold !text-gray-800 !flex-shrink-0">
        {menu}
        <span className="!text-sm !text-orange-500 !ml-2">{isDev}</span>
      </h2>

      {/* 페이지 알림 메시지 - 제목 옆에 표시 */}
      {alertType && alertMessage && (
        <div
          className={`!flex !items-center !gap-4 !px-6 !py-3 !rounded-xl !border-2 !transition-colors ${
            alertType === 'success'
              ? '!bg-green-50 !border-green-400'
              : alertType === 'error'
              ? '!bg-red-50 !border-red-400 !cursor-pointer hover:!bg-red-100'
              : '!bg-blue-50 !border-blue-400'
          }`}
          onClick={onAlertClick || undefined}
        >
          <FontAwesomeIcon
            icon={
              alertType === 'success'
                ? faCheckCircle
                : alertType === 'error'
                ? faExclamationCircle
                : faSpinner
            }
            className={`!text-2xl !flex-shrink-0 ${
              alertType === 'success'
                ? '!text-green-600'
                : alertType === 'error'
                ? '!text-red-600'
                : '!text-blue-600 !animate-spin'
            }`}
          />
          <span
            className={`!text-lg !font-bold !whitespace-nowrap ${
              alertType === 'success'
                ? '!text-green-800'
                : alertType === 'error'
                ? '!text-red-800'
                : '!text-blue-800'
            }`}
          >
            {alertMessage} {alertSubMessage}
          </span>
        </div>
      )}

      {/* 보조 버튼 (실시간 등록 등) - store에서 설정된 경우 표시 */}
      {showSecondaryButton && secondaryButtonLabel && (
        <button
          onClick={onSecondaryButtonClick || undefined}
          disabled={secondaryButtonDisabled || secondaryButtonLoading}
          className={`!px-6 !py-2.5 !text-white !rounded-lg !font-semibold !text-base !transition-colors !flex-shrink-0 !whitespace-nowrap ${
            secondaryButtonDisabled
              ? '!bg-gray-400 !cursor-not-allowed'
              : '!bg-orange-500 hover:!bg-orange-600'
          }`}
        >
          {secondaryButtonLoading ? (
            <span className="!flex !items-center !gap-2">
              <FontAwesomeIcon icon={faSpinner} className="!animate-spin" />
              등록 중...
            </span>
          ) : (
            secondaryButtonLabel
          )}
        </button>
      )}

      {/* 메인 등록 버튼 - store에서 설정된 경우 표시 */}
      {showButton && buttonLabel && (
        <button
          onClick={onButtonClick || undefined}
          disabled={buttonLoading}
          className="!px-6 !py-2.5 !bg-green-500 hover:!bg-green-600 !text-white !rounded-lg !font-semibold !text-base !transition-colors disabled:!bg-gray-400 !flex-shrink-0 !whitespace-nowrap"
        >
          {buttonLoading ? (
            <span className="!flex !items-center !gap-2">
              <FontAwesomeIcon icon={faSpinner} className="!animate-spin" />
              등록 중...
            </span>
          ) : (
            buttonLabel
          )}
        </button>
      )}

      {/* iframe 토글 버튼 - iframe 설정이 있는 페이지에서만 표시 */}
      {iframeConfig && (
        <button
          onClick={handleToggleIframe}
          className={`!px-8 !py-2 !rounded-xl !flex !items-center !justify-center !gap-4 !transition-all !duration-300 !ease-in-out !border-3 !min-w-[400px] !relative !overflow-hidden
            ${(isOpen && !isAutoOpened) 
              ? '!bg-red-600 !text-white !border-red-700 hover:!bg-red-700 hover:!border-red-800' 
              : '!bg-orange-500 !text-white !border-orange-600 hover:!bg-orange-600 hover:!border-orange-700 hover:!shadow-orange-500/50'
            } !font-black !shadow-xl hover:!shadow-2xl !transform`}
          style={{
            fontSize: '24px',
            boxShadow: (isOpen && !isAutoOpened) 
              ? '0 0 25px rgba(239, 68, 68, 0.4)' 
              : '0 0 25px rgba(251, 146, 60, 0.4)',
            animation: (!isOpen || isAutoOpened) ? 'gentlePulse 3s ease-in-out infinite' : 'none'
          }}
        >        
          <span className="!font-black !z-10 !relative">
            {/* 새창으로 여는 페이지(/bizmsg/sender)는 항상 버튼 텍스트 표시 */}
            {iframeConfig.openInNewTab 
              ? iframeConfig.buttonText 
              : (isOpen ? iframeConfig.closeText : iframeConfig.buttonText)
            }
          </span>
          
          {/* 부드러운 반짝임 효과 */}
          {(!isOpen || isAutoOpened) && (
            <div className="!absolute !top-0 !left-0 !w-full !h-full !bg-gradient-to-r !from-transparent !via-white/20 !to-transparent !transform !skew-x-12 !opacity-0 hover:!opacity-100 !transition-opacity !duration-500 !z-10"></div>
          )}
        </button>
      )}
      
      {/* 부드러운 점멸 애니메이션 */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes gentlePulse {
            0%, 100% { 
              box-shadow: 0 0 15px rgba(251, 146, 60, 0.2);
              opacity: 0.5;
            }
            50% { 
              box-shadow: 0 0 35px rgba(251, 146, 60, 0.8);
              opacity: 1;
            }
          }
        `
      }} />
    </div>
  );
};

export default Title;
